import React, { useEffect, useState } from 'react'
import { Button, Drawer, Table, TablePaginationConfig, Tooltip } from 'antd'
import moment from 'moment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getPaymentsAction } from 'slices/payment'
import addCurrencySymbol from 'utils/addCurrencySymbol'
import { Payment } from 'types'
import { EditOutlined } from '@ant-design/icons'
import UpdatePaymentDetailsForm from 'component/Payments/UpdatePaymentDetailsForm'

/**
 * Hardcoded size of the table
 */
const size = 10

interface PropTypes {
	clientId: string
}

const PaymentsTable: React.FC<PropTypes> = ({ clientId }) => {
	const dispatch = useAppDispatch()
	const { records, currentPage, totalCount, loading } = useAppSelector(
		state => state.payment
	)
	const [visible, setVisible] = useState<boolean>(false)
	const [invoiceId, setInvoiceId] = useState<string>('')
	const currency =
		useAppSelector(
			state => state.singleClient.clientProfile?.currency_selected
		) || 'INR'

	/**
	 * Pagination config for the table
	 */
	const pagination = {
		current: currentPage,
		total: totalCount,
		pageSize: size,
	}

	useEffect(() => {
		const options = {
			page: pagination.current,
			size: pagination.pageSize,
			client: clientId,
		}
		dispatch(getPaymentsAction(options))
	}, [dispatch, clientId])

	/**
	 * Handles pagination change by changing the current page in the store.
	 *
	 * @param page - Pagination object from the table
	 */

	const handlePageChange = (page: TablePaginationConfig) => {
		const options = {
			page: page.current || 1,
			size: page.pageSize || size,
			client: clientId,
		}
		dispatch(getPaymentsAction(options))
	}

	/**
	 * Rerender the table after form success
	 */
	const onSuccessNewInvoice = (val: boolean) => {
		if (val) {
			setVisible(false)
			setVisible(false)
			const options = {
				page: pagination.current,
				size: pagination.pageSize,
				client: clientId,
			}
			dispatch(getPaymentsAction(options))
		}
	}
	/**
	 * Columns for the table of Invoice
	 */

	const columns = [
		{
			title: 'Payment ID',
			dataIndex: 'paymentId',
		},
		{
			title: `Amount (${currency})`,
			dataIndex: 'amount',
			render: (amount: number, record: Payment) =>
				addCurrencySymbol(amount, record.currency ?? currency),
		},
		{
			title: 'Date',
			dataIndex: 'createdAt',
			render: (createdAt: string) => moment(createdAt).format('DD/MM/YYYY'),
		},
		{
			title: 'Invoice Number',
			dataIndex: ['invoice', 'number'],
		},
		{
			title: 'Transaction Mode',
			dataIndex: 'txnMode',
		},
		{
			title: 'Transaction Channel',
			dataIndex: 'txnChannel',
		},
		{
			title: 'Transaction Type',
			dataIndex: 'txnType',
		},
		{
			title: 'Remarks',
			dataIndex: 'remarks',
		},
		{
			title: 'Receipt',
			dataIndex: 'url',
			width: 180,
		},
		{
			title: 'Action',
			key: 'action',
			dataIndex: '_id',
			fixed: 'right' as const,
			width: 100,
			render: (_: string, row: Payment) => (
				<div style={{ display: 'flex' }}>
					<div>
						<Tooltip placement='bottomLeft' title='Update url'>
							<Button
								type='primary'
								icon={<EditOutlined />}
								onClick={() => {
									setVisible(true)
									setInvoiceId(row._id)
								}}
							/>
						</Tooltip>
					</div>
				</div>
			),
		},
	]

	return (
		<div>
			<Table
				columns={columns}
				rowKey={record => record._id}
				dataSource={records}
				pagination={{ ...pagination, position: ['bottomCenter'] }}
				loading={loading}
				onChange={handlePageChange}
				scroll={{ x: 'calc(100vw + 0.35rem)' }}
			/>
			<Drawer
				title='Update Invoice Details'
				placement='right'
				width='33vw'
				closable
				visible={visible}
				onClose={() => {
					setVisible(false)
				}}
				destroyOnClose
			>
				<UpdatePaymentDetailsForm
					invoiceId={invoiceId}
					onSuccess={onSuccessNewInvoice}
				/>
			</Drawer>
		</div>
	)
}

export default PaymentsTable
